
import { defineComponent, PropType } from 'vue'

import ActiveUserMixin from '~/mixins/activeUserMixin'
import AutoOnboardMixin from '~/mixins/autoOnboardMixin'
import OwnerDetailMixin from '~/mixins/ownerDetailMixin'
import { RepoConfigInterface } from '~/store/repository/detail'

export default defineComponent({
  name: 'SaveTemplateConfig',
  mixins: [AutoOnboardMixin, ActiveUserMixin, OwnerDetailMixin],
  props: {
    title: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    description: {
      type: String,
      default: ''
    },
    templateConfig: {
      type: Object as PropType<RepoConfigInterface>,
      required: true
    }
  },
  data() {
    return {
      savingConfig: false,
      isSaveModalVisible: false,
      internalTitle: '',
      internalDescription: ''
    }
  },
  watch: {
    title: {
      immediate: true,
      handler(newValue) {
        this.internalTitle = newValue || ''
      }
    },
    description: {
      immediate: true,
      handler(newValue) {
        this.internalDescription = newValue || ''
      }
    }
  },
  computed: {
    baseConfig(): {
      title: string
      description: string
      config: string
    } {
      return {
        title: this.internalTitle,
        description: this.internalDescription,
        config: JSON.stringify(this.templateConfig)
      }
    }
  },
  inject: {
    validateConfigForAnalyzers: {
      from: 'validateConfigForAnalyzers',
      default: () => () => false // provide a default value in case the injection fails
    }
  },
  methods: {
    async updateConfig(close: () => void): Promise<void> {
      if (!this.internalTitle) {
        this.$toast.info('Please enter a title for this template')
        return
      }
      this.savingConfig = true
      try {
        const { shortcode } = this.$route.params
        const res = await this.updateConfigTemplate({
          ...this.baseConfig,
          shortcode
        })
        if (res.ok) {
          await this.refetch(shortcode)
          this.$toast.success('Config template saved successfully')
          this.$emit('refetch')
          close()
        } else {
          throw new Error('Unable to update config template')
        }
      } catch (e) {
        this.$toast.danger(
          'There was a problem updating this config, please check your config for errors or contact support.'
        )
        this.logErrorForUser(e as Error, 'Auto Onboard template update', this.baseConfig)
      } finally {
        this.savingConfig = false
      }
    },
    async refetch(shortcode?: string): Promise<void> {
      const { provider, owner } = this.$route.params
      await this.refetchTemplateList()
      if (shortcode) {
        await this.fetchSingleTemplate({
          provider,
          shortcode,
          login: owner,
          refetch: true
        })
      }
    },
    openModal() {
      // @ts-expect-error - validateConfigForAnalyzers is injected
      const validity = this.validateConfigForAnalyzers()
      if (validity) {
        this.isSaveModalVisible = true
      } else {
        this.$toast.danger(
          "Your configuration is invalid, please ensure you've filled all required fields"
        )
      }
    }
  }
})
